import { Trophy, Ban } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmptyState } from '@/components/ui/empty-state';
import { useEventStandings } from '@/api/extensions/useScoringOps';
import type { StandingEntry } from '@/api/extensions/useScoringOps';
import { useEventDetail } from '@/api/extensions/useRodeoEvents';
import { resolveEventScoring, timedScoreDecimals } from '@/constants/event-types';
import { countPrizeParticipants, formatPrizeWithShare } from '@/utils/prize-share';

interface StandingsPanelProps {
  eventId: string;
  roundFilter: number | null;
  onRoundFilterChange: (round: number | null) => void;
  maxRounds?: number;
}

const rankStyles: Record<number, string> = {
  1: 'text-yellow-600 font-bold',
  2: 'text-gray-500 font-bold',
  3: 'text-orange-600 font-bold',
};

export function StandingsPanel({
  eventId,
  roundFilter,
  onRoundFilterChange,
  maxRounds = 5,
}: StandingsPanelProps) {
  // The round dropdown is a SCOPE selector: "Average (All Rounds)" ranks by
  // cumulative standing and pays the Average prizes; "Round N" re-ranks by that
  // round alone and pays that round's prizes. `roundFilter` null = Average.
  const { data: standingsResponse, isLoading } = useEventStandings(eventId, {
    round: roundFilter ?? undefined,
  });
  const { data: eventDetail } = useEventDetail(eventId);

  // Jackpots are always timed team-roping; only rodeo events carry a discipline.
  const isRodeoEvent = !!(eventDetail as any)?.rodeo_id;
  const scoring = resolveEventScoring(eventDetail as any);
  const isTeamRoping = isRodeoEvent ? scoring.isTeamRoping : true;
  const isScored = isRodeoEvent ? scoring.isScored : false;

  const standingsList: StandingEntry[] =
    (standingsResponse as any)?.data ?? (standingsResponse as unknown as any[]) ?? [];

  // Only surface the prize column when this scope actually pays a prize.
  const hasPrizes = standingsList.some((s) => (s.prize_amount ?? 0) > 0);
  const prizeHeading = roundFilter != null ? `Round ${roundFilter} Prize` : 'Avg Prize';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Standings</h3>
        <Select
          value={roundFilter != null ? String(roundFilter) : 'all'}
          onValueChange={(v) => onRoundFilterChange(v === 'all' ? null : parseInt(v))}
        >
          <SelectTrigger className="w-44 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Average (All Rounds)</SelectItem>
            {Array.from({ length: maxRounds }, (_, i) => i + 1).map((r) => (
              <SelectItem key={r} value={String(r)}>
                Round {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : standingsList.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title="No standings yet"
          description="Standings will appear once scores are recorded."
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">Rank</TableHead>
                <TableHead className="w-16">{isTeamRoping ? 'Team #' : '#'}</TableHead>
                {isTeamRoping ? (
                  <>
                    <TableHead>Header</TableHead>
                    <TableHead>Heeler</TableHead>
                  </>
                ) : (
                  <TableHead>Contestant</TableHead>
                )}
                <TableHead className="text-right">{isScored ? 'Scored' : 'Catches'}</TableHead>
                <TableHead className="text-right">{isScored ? 'Total Score' : 'Total Time'}</TableHead>
                {hasPrizes && <TableHead className="text-right">{prizeHeading}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {standingsList.map((s) => (
                <TableRow key={s.team_id}>
                  <TableCell
                    className={`text-sm ${s.did_not_perform ? 'text-purple-600' : rankStyles[s.placement] ?? ''}`}
                  >
                    {s.did_not_perform ? 'DNP' : s.placement}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    <span className="inline-flex items-center gap-1">
                      {s.team_number}
                      {s.excluded_from_prize_pool && (
                        <span title="Excluded from prize pool" aria-label="Excluded from prize pool">
                          <Ban className="h-3 w-3 text-slate-500" />
                        </span>
                      )}
                    </span>
                  </TableCell>
                  {isTeamRoping ? (
                    <>
                      <TableCell className="text-sm">
                        {s.member_names?.[0] ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {s.member_names?.[1] ?? '—'}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell className="text-sm">
                      {s.member_names?.[0] ?? '—'}
                    </TableCell>
                  )}
                  <TableCell className="text-right text-sm">
                    {s.successful_catches}/{s.total_runs}
                  </TableCell>
                  <TableCell className={`text-right text-sm font-mono ${s.did_not_perform ? 'text-purple-600' : ''}`}>
                    {s.total_time != null
                      ? `${Number(s.total_time).toFixed(timedScoreDecimals(scoring.isBarrelRacing))}${isScored ? '' : 's'}`
                      : s.did_not_perform ? 'DNP' : isScored ? 'NS' : 'NT'}
                  </TableCell>
                  {hasPrizes && (
                    <TableCell className="text-right text-sm font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatPrizeWithShare(s.prize_amount, countPrizeParticipants(s.member_names))}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
